/**
 * Shared hook that resolves the user's Ghostwriter consent status.
 *
 * Resolution cascade (runs once per user session; re-runs after store
 * is cleared on user switch):
 *   1. Store already populated → no-op
 *   2. TareBridge local storage → use cached value
 *   3. Ghostwriter backend → fetch, persist locally, then store
 *   4. On error → store an error state (data stays null) so the result is
 *      NOT cached; the UI still falls back to hasConsented = false, and the
 *      next mount retries. This keeps transient fetch/storage failures
 *      self-healing rather than locking in a permanent "no consent".
 *
 * Used by both useGhostwriterForViewJob and useGhostwriterForOnboarding
 * so consent is resolved regardless of which screen mounts first.
 */

import type { ScheduleApplyConsentStatusResponse } from "../models/ScheduleApplyConsentStatus";

import {
  loadingAsyncState,
  successAsyncState,
  errorAsyncState,
  type AsyncState,
} from "./AsyncState";
import { useGhostwriterApi, getLocalConsent, setLocalConsent } from "./ghostwriterApi";
import { useGhostwriterConsentStatus } from "./GhostwriterContext";
import { setConsentStatusState } from "./GhostwriterStore";

import { useEffect, useRef } from "react";

export interface ResolvedConsentStatus {
  hasConsented: boolean;
  consentLoading: boolean;
  consentError: Error | null;
  consentStatus: AsyncState<ScheduleApplyConsentStatusResponse>;
}

export function useResolveConsentStatus(enabled = true): ResolvedConsentStatus {
  const { fetchConsentStatus } = useGhostwriterApi();
  const consentStatus = useGhostwriterConsentStatus();
  const didFetchRef = useRef(false);

  useEffect(() => {
    if (!enabled || consentStatus.loading) return;
    // Any settled result is authoritative for the session — re-resolve only
    // after the store is cleared on user switch. A bare `hasConsented` check
    // would treat a resolved `false` as unresolved and re-hit the backend on
    // every ViewJob remount for non-consented users. Mid-session consent
    // flips the store to `true` via useGhostwriterForOnboarding, so caching
    // the negative result is safe and self-correcting. See RN-5820.
    if (consentStatus.data !== null) return;
    if (didFetchRef.current) return;

    didFetchRef.current = true;
    setConsentStatusState(loadingAsyncState<ScheduleApplyConsentStatusResponse>());

    void (async () => {
      try {
        const localValue = await getLocalConsent();

        // Accept a local true immediately; if false/null, check the
        // backend — the user may have consented on a different platform.
        // This cross-platform check runs once per session (the
        // `data !== null` guard above caches the settled result), not
        // on every ViewJob mount.
        if (localValue === true) {
          setConsentStatusState(successAsyncState({ hasConsented: true }));
          return;
        }

        const backendConsent = await fetchConsentStatus();
        await setLocalConsent(backendConsent.hasConsented);
        setConsentStatusState(successAsyncState({ hasConsented: backendConsent.hasConsented }));
      } catch (err) {
        // Do NOT cache an error-derived result as a settled decision —
        // a transient fetch/storage failure must not become a permanent
        // session-wide "no consent". Storing an error state (data: null)
        // leaves the guard above treating consent as unresolved, so the
        // next mount retries. `didFetchRef` is intentionally left set to
        // avoid an immediate same-mount refetch loop (a fresh mount gets
        // a new ref and is the natural retry point). See RN-5820.
        setConsentStatusState(
          errorAsyncState<ScheduleApplyConsentStatusResponse>(
            err instanceof Error ? err : new Error("Failed to resolve consent"),
          ),
        );
      }
    })();
  }, [enabled, consentStatus.data, consentStatus.loading, fetchConsentStatus]);

  return {
    hasConsented: consentStatus.data?.hasConsented ?? false,
    consentLoading: consentStatus.loading,
    consentError: consentStatus.error,
    consentStatus,
  };
}
